import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Download, Trash2, ClipboardList, MessageSquareText } from "lucide-react";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function AdminLeads() {
  const { token } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    axios.get(`${API}/admin/leads`, { withCredentials: true, headers })
      .then((r) => setLeads(r.data || []))
      .catch(() => toast.error("Could not load leads"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDownload = async () => {
    try {
      const res = await axios.get(`${API}/admin/leads/export`, {
        withCredentials: true, headers, responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "freelanceblogger-leads.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Leads CSV downloaded");
    } catch {
      toast.error("Download failed");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/leads/${id}`, { withCredentials: true, headers });
      setLeads((prev) => prev.filter((l) => l.id !== id));
      setDeleteId(null);
      toast.success("Lead removed");
    } catch {
      toast.error("Failed to remove lead");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-24 pb-16 px-4 sm:px-6 max-w-5xl mx-auto">
        <div className="flex items-center justify-between gap-3 mb-8">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
              <ArrowLeft size={18} />
            </Link>
            <div>
              <p className="overline mb-1">Admin</p>
              <h1 className="font-cormorant text-3xl font-semibold text-foreground">Leads</h1>
            </div>
          </div>
          <button
            onClick={handleDownload}
            disabled={leads.length === 0}
            data-testid="download-leads-csv-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-brand-accent text-white font-manrope text-sm font-medium hover:bg-brand-accent-hover disabled:opacity-50 transition-colors"
          >
            <Download size={15} />
            Download CSV
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-2 border-brand-accent border-t-transparent rounded-full animate-spin" />
          </div>
        ) : leads.length === 0 ? (
          <div className="text-center py-16 bg-card border border-border rounded-xl" data-testid="leads-empty">
            <ClipboardList size={32} className="text-muted-foreground mx-auto mb-3" />
            <p className="font-manrope text-sm text-muted-foreground">
              No leads yet. Share your homepage — the "Let's keep in touch" form feeds this list.
            </p>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="leads-table">
                <thead className="border-b border-border bg-muted/40">
                  <tr>
                    <th className="text-left py-3 px-5 font-manrope text-xs font-semibold text-muted-foreground">Name</th>
                    <th className="text-left py-3 px-5 font-manrope text-xs font-semibold text-muted-foreground">Email</th>
                    <th className="text-left py-3 px-5 font-manrope text-xs font-semibold text-muted-foreground hidden sm:table-cell">Phone</th>
                    <th className="text-left py-3 px-5 font-manrope text-xs font-semibold text-muted-foreground">SMS</th>
                    <th className="text-left py-3 px-5 font-manrope text-xs font-semibold text-muted-foreground hidden md:table-cell">Source</th>
                    <th className="text-left py-3 px-5 font-manrope text-xs font-semibold text-muted-foreground hidden sm:table-cell">Added</th>
                    <th className="py-3 px-5"></th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => (
                    <tr key={lead.id} className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors" data-testid={`lead-row-${lead.id}`}>
                      <td className="py-3.5 px-5 font-manrope text-sm text-foreground">
                        {lead.first_name} {lead.last_name}
                      </td>
                      <td className="py-3.5 px-5 font-manrope text-sm text-muted-foreground">{lead.email}</td>
                      <td className="py-3.5 px-5 font-manrope text-sm text-muted-foreground hidden sm:table-cell">{lead.phone || "—"}</td>
                      <td className="py-3.5 px-5">
                        {lead.sms_opt_in ? (
                          <span className="inline-flex items-center gap-1 font-manrope text-xs px-2 py-1 rounded-full bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400">
                            <MessageSquareText size={11} /> Yes
                          </span>
                        ) : (
                          <span className="font-manrope text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">No</span>
                        )}
                      </td>
                      <td className="py-3.5 px-5 hidden md:table-cell">
                        <span className="font-manrope text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">{lead.source || "website"}</span>
                      </td>
                      <td className="py-3.5 px-5 font-manrope text-xs text-muted-foreground hidden sm:table-cell">{formatDate(lead.created_at)}</td>
                      <td className="py-3.5 px-5 text-right">
                        {deleteId === lead.id ? (
                          <span className="inline-flex gap-2">
                            <button onClick={() => handleDelete(lead.id)} data-testid={`confirm-delete-lead-${lead.id}`} className="font-manrope text-xs text-destructive font-semibold">Confirm</button>
                            <button onClick={() => setDeleteId(null)} className="font-manrope text-xs text-muted-foreground">Cancel</button>
                          </span>
                        ) : (
                          <button onClick={() => setDeleteId(lead.id)} data-testid={`delete-lead-${lead.id}`} className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors">
                            <Trash2 size={15} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
